import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Textarea,
} from '@wundo/ui';
import { LanguageSelect } from '@/components/LanguageSelect.tsx';
import type { CreateTemplateRequest, Template } from '@/types/broadcast.ts';
import {
  TEMPLATE_BODY_LIMIT,
  emptyTemplateForm,
  hasTemplateFormErrors,
  noTemplateFormErrors,
  validateTemplateForm,
  type TemplateFormData,
} from './templateForm.ts';

export interface TemplateEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The template being edited; null when a new one is being written. */
  template: Template | null;
  saving: boolean;
  /** Shown in the dialog when the last save failed; empty while it has not. */
  error: string;
  onSave: (request: CreateTemplateRequest) => void;
}

/**
 * The template editor, which writes both a new template and an existing one.
 *
 * The name is the key the backend stores it under, so editing cannot change it
 * — a renamed template is a new one, and the old one would stay behind.
 */
export function TemplateEditorDialog({
  open,
  onOpenChange,
  template,
  saving,
  error,
  onSave,
}: TemplateEditorDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size='lg' className='max-h-9/10 overflow-y-auto'>
        {open && (
          <TemplateForm
            /* Remounts on another template, so the fields start from that one. */
            key={template?.id ?? 'new'}
            template={template}
            saving={saving}
            error={error}
            onSave={onSave}
            onCancel={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function TemplateForm({
  template,
  saving,
  error,
  onSave,
  onCancel,
}: Pick<TemplateEditorDialogProps, 'template' | 'saving' | 'error' | 'onSave'> & {
  onCancel: () => void;
}) {
  const editing = template !== null;
  const [form, setForm] = useState<TemplateFormData>(() =>
    template
      ? {
          name: template.name,
          body: template.body,
          language: template.language,
        }
      : emptyTemplateForm(),
  );
  const [errors, setErrors] = useState(noTemplateFormErrors);

  function submit() {
    const found = validateTemplateForm(form);
    setErrors(found);
    if (hasTemplateFormErrors(found)) return;

    onSave(form);
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{editing ? 'Edit Template' : 'Create Template'}</DialogTitle>
      </DialogHeader>

      <div className='flex flex-col gap-5 px-5 py-4'>
        <Field
          label='Template Name'
          required
          description={
            editing
              ? 'Template name cannot be changed after creation.'
              : 'Lowercase letters, numbers, and underscores only. Max 100 characters.'
          }
          error={errors.name}
        >
          {(control) => (
            <Input
              {...control}
              autoFocus={!editing}
              placeholder='e.g., weekly_update'
              disabled={editing}
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          )}
        </Field>

        <Field label='Language' required>
          {(control) => (
            <LanguageSelect
              id={control.id}
              value={form.language}
              onChange={(language) => setForm({ ...form, language })}
            />
          )}
        </Field>

        <Field
          label='Template Body'
          required
          description='Static text only. No variables or buttons allowed.'
          error={errors.body}
        >
          {(control) => (
            <>
              <Textarea
                {...control}
                rows={8}
                sizing='manual'
                placeholder='Enter your template message here...'
                value={form.body}
                onChange={(event) => setForm({ ...form, body: event.target.value })}
              />
              <p
                className={
                  form.body.length > TEMPLATE_BODY_LIMIT
                    ? 'text-right text-xs font-semibold text-danger'
                    : 'text-right text-xs text-ink-muted'
                }
              >
                {form.body.length} / {TEMPLATE_BODY_LIMIT} characters
              </p>
            </>
          )}
        </Field>

        {error !== '' && (
          <p role='alert' className='text-sm text-danger'>
            {error}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button size='sm' variant='quiet' disabled={saving} onClick={onCancel}>
          Cancel
        </Button>
        <Button size='sm' loading={saving} onClick={submit}>
          {editing ? 'Update Template' : 'Create Template'}
        </Button>
      </DialogFooter>
    </>
  );
}
